import { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { trackingAPI } from '../../services/api';
import {
  Eye,
  Flame,
  TrendingUp,
  Clock,
  Building2,
  ExternalLink,
  Filter,
} from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

export default function BuyerInterest() {
  const { userProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, hot, warm, cold
  const [interests, setInterests] = useState([]);
  const [stats, setStats] = useState({});
  const [dailyViews, setDailyViews] = useState([]);
  const [activity, setActivity] = useState([]);

  const sellerId = userProfile?.seller_id;

  useEffect(() => {
    if (sellerId) {
      loadData();
    } else {
      setLoading(false);
    }
  }, [sellerId]);

  const loadData = async () => {
    try {
      const [interestRes, statsRes, activityRes] = await Promise.all([
        trackingAPI.getBuyerInterest(sellerId),
        trackingAPI.getViewStats(sellerId),
        trackingAPI.getRecentActivity(sellerId, 30),
      ]);

      setInterests(interestRes.data?.data?.interests || []);
      setStats({
        total: interestRes.data?.data?.total_interested_buyers || 0,
        hot: interestRes.data?.data?.hot_leads || 0,
        warm: interestRes.data?.data?.warm_leads || 0,
      });
      setDailyViews(statsRes.data?.data?.dailyViews || []);
      setActivity(activityRes.data?.data || []);
    } catch (error) {
      console.error('Failed to load interest data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredInterests = interests.filter((i) => {
    if (filter === 'all') return true;
    return i.interest_level === filter;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Buyer Interest</h1>
        <p className="text-gray-600">
          Track which buyers are viewing your profile and their interest level.
        </p>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-3 gap-4">
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <Flame className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.hot || 0}</p>
              <p className="text-sm text-gray-600">Hot Leads</p>
            </div>
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.warm || 0}</p>
              <p className="text-sm text-gray-600">Warm Leads</p>
            </div>
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Eye className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.total || 0}</p>
              <p className="text-sm text-gray-600">Total Buyers</p>
            </div>
          </div>
        </div>
      </div>

      {/* Views Chart */}
      {dailyViews.length > 0 && (
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Profile Views (Last 30 Days)
          </h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={dailyViews}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="date"
                  tickFormatter={(date) => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  stroke="#9ca3af"
                  fontSize={12}
                />
                <YAxis stroke="#9ca3af" fontSize={12} />
                <Tooltip
                  contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
                  labelFormatter={(date) => new Date(date).toLocaleDateString()}
                />
                <Line
                  type="monotone"
                  dataKey="views"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  dot={{ fill: '#3b82f6', strokeWidth: 2, r: 4 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Interested Buyers List */}
      <div className="card">
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">
              Interested Buyers
            </h2>
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-500" />
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="text-sm border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="all">All Buyers</option>
                <option value="hot">Hot Leads</option>
                <option value="warm">Warm Leads</option>
                <option value="cold">Cold Leads</option>
              </select>
            </div>
          </div>
        </div>

        {filteredInterests.length > 0 ? (
          <div className="divide-y divide-gray-100">
            {filteredInterests.map((buyer) => (
              <BuyerCard key={buyer.buyer_id} buyer={buyer} />
            ))}
          </div>
        ) : (
          <div className="p-12 text-center">
            <Eye className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No buyer interest recorded yet</p>
            <p className="text-sm text-gray-400 mt-1">
              When buyers view your profile, they'll appear here
            </p>
          </div>
        )}
      </div>

      {/* Recent Activity Timeline */}
      {activity.length > 0 && (
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Recent Activity
          </h2>
          <div className="space-y-4">
            {activity.slice(0, 10).map((item, index) => (
              <div key={item.id || index} className="flex items-start gap-3">
                <div className="w-2 h-2 bg-primary-600 rounded-full mt-2" />
                <div className="flex-1">
                  <p className="text-sm text-gray-900">{item.message}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <Clock className="w-3 h-3 text-gray-400" />
                    <span className="text-xs text-gray-500">
                      {new Date(item.timestamp).toLocaleString()}
                    </span>
                    {item.duration > 0 && (
                      <span className="text-xs text-gray-500">
                        • Viewed for {Math.floor(item.duration / 60)}m {item.duration % 60}s
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function BuyerCard({ buyer }) {
  const [expanded, setExpanded] = useState(false);

  const levelColors = {
    hot: 'border-l-red-500',
    warm: 'border-l-yellow-500',
    cold: 'border-l-blue-500',
  };

  return (
    <div
      className={`p-6 border-l-4 ${levelColors[buyer.interest_level]} hover:bg-gray-50 cursor-pointer transition-colors`}
      onClick={() => setExpanded(!expanded)}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
            <Building2 className="w-6 h-6 text-gray-500" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">{buyer.company_name}</h3>
            <p className="text-sm text-gray-500">{buyer.headquarters}</p>
            <div className="flex items-center gap-4 mt-2 text-sm">
              <span className="text-gray-600">
                <strong>{buyer.view_count}</strong> views
              </span>
              <span className="text-gray-600">
                <strong>{Math.floor((buyer.total_duration || 0) / 60)}</strong> min total
              </span>
              <span className="text-gray-600">
                Last: {new Date(buyer.last_viewed).toLocaleDateString()}
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <InterestScore score={buyer.interest_score} level={buyer.interest_level} />
        </div>
      </div>

      {expanded && (
        <div className="mt-4 pt-4 border-t border-gray-100">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">
                Therapeutic Areas
              </p>
              <div className="flex flex-wrap gap-1">
                {buyer.therapeutic_areas?.map((area) => (
                  <span
                    key={area}
                    className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded"
                  >
                    {area}
                  </span>
                ))}
              </div>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">
                Tech Platforms
              </p>
              <div className="flex flex-wrap gap-1">
                {buyer.tech_platforms?.map((platform) => (
                  <span
                    key={platform}
                    className="px-2 py-1 bg-green-50 text-green-700 text-xs rounded"
                  >
                    {platform}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {buyer.interest_level === 'hot' && (
            <div className="mt-4 p-3 bg-red-50 rounded-lg">
              <p className="text-sm text-red-800">
                <strong>🔥 Hot Lead!</strong> This buyer has shown strong interest.
                Consider reaching out soon.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function InterestScore({ score, level }) {
  const colors = {
    hot: 'text-red-600 bg-red-100',
    warm: 'text-yellow-600 bg-yellow-100',
    cold: 'text-blue-600 bg-blue-100',
  };

  return (
    <div className={`px-3 py-1 rounded-lg ${colors[level]}`}>
      <span className="font-bold">{score}</span>
      <span className="text-sm">/100</span>
    </div>
  );
}
